import type { FetchInputType } from "./types/fetch-params";
import { getInAppWebview } from "./detect-webview";
import { stringifyInput } from "./vercel-proxy";
import type { LogEvent } from "~/hooks/useLogEvent";
import { describeError } from "~/utils/errorTelemetry";

/**
 * Stable, filterable reason a thrown client-side fetch failure is classified
 * into for observability (see `classifyFetchFailure`). `timeout` exists only
 * because the api client sets its own 15s deadline.
 */
export const FETCH_FAILURE_REASON = {
    /** A per-request or caller timeout signal fired: DOMException "TimeoutError". */
    TIMEOUT: "timeout",
    /** `navigator.onLine === false`, so the request never reached the backend. */
    OFFLINE: "offline",
    /**
     * An explicit caller / navigation cancellation (component unmount,
     * superseded request). DOMException "AbortError". Treated as benign and
     * NOT reported to the web error beacon.
     */
    ABORTED: "aborted",
    /** Any other throw: `TypeError: Failed to fetch`, CORS, DNS, resets. */
    NETWORK: "network",
} as const;

export type FetchFailureReason =
    (typeof FETCH_FAILURE_REASON)[keyof typeof FETCH_FAILURE_REASON];

// Classifies a thrown fetch failure into a stable, filterable reason for
// observability. A pure string mapping; the caller uses the result to (a) tag
// the `api-error` log and (b) short-circuit benign cancellations. Order
// matters: "timeout" (our own deadline) and "offline" are checked before the
// generic "aborted"/"network" buckets.
/**
 * Whether the device reports no connectivity. Exported for callers that have
 * to judge a failure with no error object — a request layer that reports the
 * outcome as a status — and so cannot call `classifyFetchFailure`.
 *
 * Only the `false` reading is trusted: `onLine === true` merely means an
 * interface exists, captive portal included.
 */
export function isDeviceOffline(): boolean {
    return (
        typeof navigator !== "undefined" &&
        "onLine" in navigator &&
        navigator.onLine === false
    );
}

/**
 * Whether a thrown failure is a deliberate cancellation. Exported separately
 * because `classifyFetchFailure` checks connectivity before the abort name on
 * purpose: a caller that cancels its own requests routinely needs the opposite
 * precedence, and `classifyFetchFailure(e) === ABORTED` would silently stop
 * recognising the cancellations of every offline customer.
 */
export function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === "AbortError";
}

export function classifyFetchFailure(error: unknown): FetchFailureReason {
    const name = error instanceof Error ? error.name : "";

    if (name === "TimeoutError") return FETCH_FAILURE_REASON.TIMEOUT;
    if (isDeviceOffline()) return FETCH_FAILURE_REASON.OFFLINE;
    if (isAbortError(error)) return FETCH_FAILURE_REASON.ABORTED;
    return FETCH_FAILURE_REASON.NETWORK;
}

/**
 * Whether a thrown fetch failure belongs to the customer rather than to us,
 * used to pick a log level so the error level stays a list of fixable things.
 *
 *  - `offline` — the request never left the device.
 *  - `aborted` — a navigation or an unmount cancelled it deliberately.
 *  - a throw with no message, or one that is not an `Error` at all — the
 *    artefact WebKit leaves behind when it tears a request off a page being
 *    navigated away from.
 *
 * `timeout` is deliberately absent: our own deadline expiring is our problem.
 * Thrown failures only — a 4xx/5xx that came back normally is a response, and
 * whoever made the request judges it.
 */
export function isBenignFetchFailure(
    error: unknown,
    failureReason: FetchFailureReason = classifyFetchFailure(error)
): boolean {
    if (
        failureReason === FETCH_FAILURE_REASON.OFFLINE ||
        failureReason === FETCH_FAILURE_REASON.ABORTED
    ) {
        return true;
    }

    return !(error instanceof Error) || !error.message;
}

// Emits a low-noise observability breadcrumb for a BENIGN fetch failure
// (caller/navigation cancellation, our own timeout, offline, or a WebKit abort
// that serializes to `{}`). These are NOT real errors — the request completes
// fine server-side — so this deliberately:
//   • logs at INFO (default level), under a dedicated `api-request-cancelled`
//     tag so it does not pollute the `api-error` baseline, and
//   • does NOT fire `window.InnerBalanceClientApi.sendWebEvent`, keeping the
//     web error beacon (BigQuery `ods.prod_web_errors`) limited to real errors.
// We still record volume + reason so a genuine spike (e.g. a backend slowdown
// driving a wave of timeouts) stays visible in Datadog without alerting noise.
export function logFetchCancellation({
    logEvent,
    error,
    failureReason,
    input,
    userAgent,
    userId,
}: {
    logEvent: LogEvent;
    error: unknown;
    failureReason: FetchFailureReason;
    input: FetchInputType;
    userAgent: string | undefined;
    userId: string | null | undefined;
}): void {
    const { errorName, errorMessage } = describeError(error);

    logEvent({
        // Whoever threw wrote this, and an http client that quotes the request
        // it failed on quotes the body with it. It lands in the event title, so
        // it is laundered before Datadog can retain and group on it.
        message: errorMessage,
        tag: "api-request-cancelled",
        data: {
            failure_reason: failureReason,
            in_app_webview: getInAppWebview(userAgent),
            error_name: errorName,
            // Normalize to a readable string (string→as-is, URL→href) instead
            // of serializing a URL/Request object into the Datadog payload.
            url: stringifyInput(input),
            href: typeof window !== "undefined" ? window.location.href : undefined,
            user_id: userId,
        },
    });
}
